import axios from 'axios';

// Create axios instance for leave services  
const api = axios.create({
  baseURL: (import.meta.env.VITE_API_URL || 'http://localhost:5000/api') + '/leave',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const leaveService = {
  // Leave Types (for employees)
  getLeaveTypes: async () => {
    const response = await api.get('/types');
    return response.data;
  },

  // Leave Types Management (for HR/Admin)
  getLeaveTypesManage: async (params = {}) => {
    const response = await api.get('/types/manage', { params });
    return response.data;
  },

  getLeaveTypeManage: async (id) => {
    const response = await api.get(`/types/manage/${id}`);
    return response.data;
  },

  createLeaveType: async (data) => {
    const response = await api.post('/types/manage', data);
    return response.data;
  },

  updateLeaveType: async (id, data) => {
    const response = await api.put(`/types/manage/${id}`, data);
    return response.data;
  },

  deleteLeaveType: async (id) => {
    const response = await api.delete(`/types/manage/${id}`);
    return response.data;
  },

  toggleLeaveTypeStatus: async (id) => {
    const response = await api.patch(`/types/manage/${id}/toggle`);
    return response.data;
  },

  getLeaveTypeStatistics: async (id, year) => {
    const params = year ? { year } : {};
    const response = await api.get(`/types/manage/${id}/statistics`, { params });
    return response.data;
  },

  // Leave Balance
  getLeaveBalance: async (userId = null) => {
    const url = userId ? `/balance/${userId}` : '/balance';
    const response = await api.get(url);
    return response.data;
  },

  // Team Calendar (for managers)
  getTeamCalendar: async (year = new Date().getFullYear(), month = null) => {
    const params = { year };
    if (month) params.month = month;
    
    const response = await api.get('/team-calendar', { params });
    return response.data;
  },

  // Leave Requests
  getLeaveRequests: async (params = {}) => {
    const response = await api.get('/requests', { params });
    return response.data;
  },

  getLeaveRequest: async (id) => {
    const response = await api.get(`/requests/${id}`);
    return response.data;
  },

  createLeaveRequest: async (data) => {
    // If there are files, use FormData
    if (data.attachments && data.attachments.length > 0) {
      const formData = new FormData();
      
      // Append text fields
      Object.keys(data).forEach(key => {
        if (key !== 'attachments') {
          formData.append(key, data[key]);
        }
      });
      
      // Append files
      data.attachments.forEach(file => {
        formData.append('attachments', file);
      });

      const response = await api.post('/requests', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } else {
      const response = await api.post('/requests', data);
      return response.data;
    }
  },

  updateLeaveRequest: async (id, data) => {
    const response = await api.put(`/requests/${id}`, data);
    return response.data;
  },

  deleteLeaveRequest: async (id) => {
    const response = await api.delete(`/requests/${id}`);
    return response.data;
  },

  // Approval Actions
  approveLeaveRequest: async (id, comments = '') => {
    const response = await api.post(`/requests/${id}/process`, {
      action: 'approve',
      comments
    });
    return response.data;
  },

  rejectLeaveRequest: async (id, comments = '') => {
    const response = await api.post(`/requests/${id}/process`, {
      action: 'reject',
      comments
    });
    return response.data;
  },

  processLeaveRequest: async (id, data) => {
    const response = await api.post(`/requests/${id}/process`, data);
    return response.data;
  },

  bulkProcessLeaveRequests: async (requestIds, action, comments = '') => {
    const response = await api.post('/requests/bulk-process', {
      requestIds,
      action,
      comments
    });
    return response.data;
  },

  // Utility functions
  calculateLeaveDays: (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const timeDiff = end.getTime() - start.getTime();
    return Math.ceil(timeDiff / (1000 * 3600 * 24)) + 1;
  },

  formatLeaveStatus: (status) => {
    const statusMap = {
      'pending': 'Pending Manager Approval',
      'approved_by_manager': 'Pending HR Approval',
      'approved_by_hr': 'Approved',
      'approved': 'Approved',
      'rejected': 'Rejected',
      'cancelled': 'Cancelled'
    };
    return statusMap[status] || status;
  },

  getStatusColor: (status) => {
    const colorMap = {
      'pending': 'yellow',
      'approved_by_manager': 'blue',
      'approved_by_hr': 'green',
      'approved': 'green',
      'rejected': 'red',
      'cancelled': 'gray'
    };
    return colorMap[status] || 'gray';
  }
};

export default leaveService; 